import { combineReducers } from 'redux';
import { urlsWrapper } from 'config';
import { serialize } from 'utils';
import { currentLocalTimestamp } from 'utils/nativeDate';
import {
  saveData,
  createCommonListConstants,
  createCommonListReducer,
  createCommonItemConstants,
  createCommonItemReducer,
} from './utils';
import { commonPut, commonDelete, commonPost, commonGet } from './api';

const scouts = createCommonListConstants('SCOUTS');
const all = createCommonListConstants('SCOUTS_ALL');
const statuses = createCommonListConstants('SCOUTS_STATUSES');
const byFieldId = createCommonListConstants('SCOUTS_BY_FIELD_ID');
const history = createCommonListConstants('SCOUTS_HISTORY');
const scoutById = createCommonItemConstants('SCOUT');

// reducer
export default combineReducers({
  scouts: createCommonListReducer(scouts),
  all: createCommonListReducer(all),
  statuses: createCommonListReducer(statuses),
  byFieldId: createCommonListReducer(byFieldId),
  history: createCommonListReducer(history),
  scoutById: createCommonItemReducer(scoutById),
});

export const setFormDataBlob = (form, blob, name) => {
  if (blob)
    form.append(
      name,
      new Blob([blob], {
        type: blob.type,
      }),
      name
    );
};

export const setPhotoScoutpoints = (scoutpoints, blob, name) => {
  if (blob) {
    scoutpoints.push({
      photo_ref: name,
      point_type: 'photo',
      timestamp: currentLocalTimestamp(),
    });
  }
};

const setCropParamsScoutpoints = (scoutpoints, cropparams = []) => {
  // Фильтруем и форматируем параметры согласно новой схеме
  const resultCropparams = cropparams
    .filter((cropparam) => {
      // Для number проверяем наличие value
      if (cropparam.type === 'number') {
        return !!cropparam?.value;
      }
      // Для choice проверяем наличие option
      if (cropparam.type === 'choice') {
        return !!cropparam?.option;
      }
      return false;
    })
    .map((item) => {
      const param = {
        text: item.text,
        type: item.type,
      };

      // Добавляем tag если он есть в схеме
      if (item.tag) {
        param.tag = item.tag;
      }

      // Для параметров с типом number указываем value
      if (item.type === 'number') {
        param.value = +item.value;
      }

      // Для параметров с типом choice указываем option
      if (item.type === 'choice' && item.option) {
        param.option = {
          text: item.option,
        };

        // Находим quality в options если есть
        const selectedOption = (item.options || []).find(
          (opt) => opt.text === item.option
        );
        if (selectedOption?.quality) {
          param.option.quality = selectedOption.quality;
        }
      }

      return param;
    });

  if (resultCropparams.length) {
    scoutpoints.push({
      data: {
        cropparams: {
          params: resultCropparams,
        },
      },
      point_type: 'scoutparams',
      timestamp: currentLocalTimestamp(),
    });
  }
};

const setProblemsScoutpoints = (scoutpoints, problems = []) => {
  if (problems.length) {
    problems.forEach((problem) => {
      const {
        devstage,
        number,
        comments,
        resistance,
        pointType,
        timestamp,
        lat,
        lon,
        problemId,
        photos,
      } = problem;
      scoutpoints.push({
        point_type: pointType,
        [`${pointType}_id`]: problemId,
        data: {
          ...(devstage ? { devstage } : {}),
          ...(typeof number === 'number' ? { number } : {}),
        },
        resistance: !!resistance,
        timestamp,
        lat: typeof lat === 'number' ? lat : null,
        lon: typeof lon === 'number' ? lon : null,
        comments: comments || '',
        photo_ref: photos?.[0]?.name || '',
      });
    });
  }
};

const getFormData = ({
  photos = [],
  cropparams = [],
  problems = [],
  ...data
}) => {
  const formData = new FormData();
  const scoutpoints = [];
  setCropParamsScoutpoints(scoutpoints, cropparams);
  setProblemsScoutpoints(scoutpoints, problems);

  Array.from(photos).forEach((file) => {
    setPhotoScoutpoints(scoutpoints, file, file.name);
  });
  formData.append(
    'meta',
    new Blob([JSON.stringify({ ...data, scoutpoints, web: true })], {
      type: 'application/json',
    }),
    'metafile'
  );
  Array.from([
    ...photos,
    ...problems.map((problem) => problem.photos?.[0]).filter(Boolean),
  ]).forEach((file) => {
    setFormDataBlob(formData, file, file.name);
  });
  return formData;
};

export const uploadScout = (scout) => () => {
  return commonPost({
    url: urlsWrapper('scoutsUpload'),
    data: getFormData(scout),
    name: 'осмотр',
  });
};

export function getScoutsAllMapAnalytics(params) {
  return commonGet({
    url: `${urlsWrapper('scoutsAll')}?${serialize(params)}`,
  });
}

export function getScoutsAll(params) {
  return saveData(
    `${urlsWrapper('scoutsAll')}?${serialize(params)}`,
    all.SET_LIST,
    all.SET_IS_PENDING
  );
}

export const updateScout = (fieldId, scoutId, data) => () =>
  commonPut({
    url: `${urlsWrapper('fields')}/${fieldId}/scouts/${scoutId}`,
    data,
    name: 'осмотр',
  });

export const deleteScout = (fieldId, scoutId) => () =>
  commonDelete({
    url: `${urlsWrapper('fields')}/${fieldId}/scouts/${scoutId}`,
  });

export function getScoutsByFieldId(fieldId) {
  return saveData(
    `${urlsWrapper('fields')}/${fieldId}/scouts`,
    byFieldId.SET_LIST,
    byFieldId.SET_IS_PENDING
  );
}

export function getScoutsByField(fieldId, params) {
  return commonGet({
    url: `${urlsWrapper('fields')}/${fieldId}/scouts?${serialize(params)}`,
  });
}

export function getScout(fieldId, scoutId, save = true) {
  const url = `${urlsWrapper('fields')}/${fieldId}/scouts/${scoutId}`;
  if (save) {
    return saveData(url, scoutById.SET_ITEM, scoutById.SET_IS_PENDING);
  }
  return commonGet({
    url,
  });
}

export const getScoutsByFieldIdSelector = (state) => state.scouts.byFieldId;
